import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from "react";
import { Table, notification } from "antd";
import { DownloadOutlined, CopyOutlined, DatabaseOutlined } from "@ant-design/icons";
import { escapeCsvValue, escapeTsvValue } from "./utils";
import { ConditionalTooltip } from "../ConditionalTooltip";

const MIN_COLUMN_WIDTH = 60;
const DEFAULT_COLUMN_WIDTH = 120;
// Small extra so the last glyph isn't kissing the right edge / sort-arrow baseline.
const CONTENT_SAFETY_PX = 4;
// Space reserved for antd's sort arrow cluster when a column is sortable.
const SORT_INDICATOR_PX = 22;

// Shared canvas for measuring text; font is configured per-measurement from the real DOM.
let measureCanvas: HTMLCanvasElement | null = null;
function ensureCanvasCtx(): CanvasRenderingContext2D | null {
    if (typeof document === 'undefined') return null;
    if (!measureCanvas) measureCanvas = document.createElement('canvas');
    return measureCanvas.getContext('2d');
}

// Compute a column's natural width from its header string, using the actual
// font and padding of a rendered <th> on the page. Caller passes the measured
// values so we don't re-read the DOM per column.
function computeWidth(col: any, font: string, paddingX: number): number {
    const title = typeof col?.title === 'string' ? col.title : '';
    if (!title) return DEFAULT_COLUMN_WIDTH;
    const ctx = ensureCanvasCtx();
    if (!ctx) return DEFAULT_COLUMN_WIDTH;
    ctx.font = font;
    const textWidth = Math.ceil(ctx.measureText(title).width);
    const sortSpace = col?.sorter ? SORT_INDICATOR_PX : 0;
    return Math.max(textWidth + paddingX + sortSpace + CONTENT_SAFETY_PX, MIN_COLUMN_WIDTH);
}

// Custom header cell with a draggable right-edge resize handle.
// antd passes through any extra props we set via onHeaderCell().
const ResizableHeaderCell = (props: any) => {
    const { onResize, width, style, children, ...restProps } = props;
    const [hovering, setHovering] = useState(false);
    const [dragging, setDragging] = useState(false);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        if (e.button !== 0 || typeof width !== 'number') return;
        const startX = e.clientX;
        const startWidth = width;
        setDragging(true);
        const onMove = (ev: MouseEvent) => {
            onResize?.(startWidth + (ev.clientX - startX));
        };
        const onUp = () => {
            setDragging(false);
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        e.preventDefault();
        e.stopPropagation();
    }, [width, onResize]);

    // If onResize/width were not provided, render a normal <th>
    if (typeof width !== 'number') {
        return <th {...restProps} style={style}>{children}</th>;
    }

    return (
        <th {...restProps} style={{ ...style, position: 'relative' }}>
            {children}
            <div
                onMouseDown={handleMouseDown}
                onMouseEnter={() => setHovering(true)}
                onMouseLeave={() => setHovering(false)}
                style={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    bottom: 0,
                    width: 8,
                    cursor: 'col-resize',
                    userSelect: 'none',
                    background: (hovering || dragging) ? 'rgba(100, 150, 255, 0.4)' : 'transparent',
                    transition: 'background 0.15s',
                    zIndex: 1,
                }}
            />
        </th>
    );
};

export function DataTableView(props: {
    columns: any[];
    dataSource: any[];
    darkMode: boolean;
    parentJobAlias?: string;
    defaultPageSize?: number;
    tableContainerRef?: React.RefObject<HTMLDivElement>;
    onAddQuery?: () => void;
}) {
    const { columns, dataSource, darkMode, parentJobAlias, defaultPageSize, tableContainerRef, onAddQuery } = props;
    const [pageSize, setPageSize] = useState(defaultPageSize ?? 100);
    const [isDownloadHovered, setIsDownloadHovered] = useState(false);
    const [pressedDownload, setPressedDownload] = useState(false);
    const [isCopyTableHovered, setIsCopyTableHovered] = useState(false);
    const [pressedCopyTable, setPressedCopyTable] = useState(false);
    const [isAddQueryHovered, setIsAddQueryHovered] = useState(false);
    const [pressedAddQuery, setPressedAddQuery] = useState(false);

    // Column widths. Initial values are a placeholder (DEFAULT_COLUMN_WIDTH) —
    // the useLayoutEffect below reads the real font & padding from a rendered
    // <th> and recomputes before the browser paints, so the placeholder is
    // never visible to the user.
    // User-resized indices are tracked separately and preserved across the
    // auto-measurement (but reset when columns change — new job, new layout).
    const [columnWidths, setColumnWidths] = useState<number[]>(() =>
        columns.map(() => DEFAULT_COLUMN_WIDTH)
    );
    const userResizedRef = useRef<Set<number>>(new Set());
    const localTableRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        userResizedRef.current = new Set();
    }, [columns]);

    useLayoutEffect(() => {
        const container = tableContainerRef?.current ?? localTableRef.current;
        if (!container) return;
        const th = container.querySelector('thead th');
        if (!th) return;
        const style = window.getComputedStyle(th);
        // getComputedStyle().font is empty in some browsers; build it by hand as fallback.
        const font = style.font && style.font.trim()
            ? style.font
            : `${style.fontStyle || 'normal'} ${style.fontVariant || 'normal'} ${style.fontWeight || 'normal'} ${style.fontSize} / ${style.lineHeight || 'normal'} ${style.fontFamily}`.trim();
        const paddingX = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);

        setColumnWidths(prev => {
            const next = columns.map((col, idx) => {
                if (userResizedRef.current.has(idx) && prev[idx] !== undefined) {
                    return prev[idx];
                }
                return computeWidth(col, font, paddingX);
            });
            return next;
        });
    }, [columns, tableContainerRef]);

    const handleResize = useCallback((index: number, newWidth: number) => {
        userResizedRef.current.add(index);
        setColumnWidths(prev => {
            if (prev[index] === undefined) return prev;
            const next = prev.slice();
            next[index] = Math.max(MIN_COLUMN_WIDTH, Math.round(newWidth));
            return next;
        });
    }, []);

    const resizableColumns = useMemo(() => {
        return columns.map((col, idx) => ({
            ...col,
            width: columnWidths[idx] ?? DEFAULT_COLUMN_WIDTH,
            onHeaderCell: () => ({
                width: columnWidths[idx] ?? DEFAULT_COLUMN_WIDTH,
                onResize: (w: number) => handleResize(idx, w),
            }),
        }));
    }, [columns, columnWidths, handleResize]);

    const tableComponents = useMemo(() => ({
        header: { cell: ResizableHeaderCell },
    }), []);

    useEffect(() => {
        if (tableContainerRef?.current) {
            const container = tableContainerRef.current;
            const cells = container.querySelectorAll('th, td');
            cells.forEach((cell) => {
                const text = cell.textContent?.trim() || '';
                if (text) {
                    cell.setAttribute('title', text);
                }
            });
        }
    }, [tableContainerRef, pageSize, columnWidths]);

    return (
        <>
            <div
                ref={(el) => {
                    localTableRef.current = el;
                    if (tableContainerRef) {
                        (tableContainerRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
                    }
                }}
                className="scrolling-table-container"
                style={{marginBottom: "1em"}}
            >
                <Table
                    size="small"
                    columns={resizableColumns}
                    components={tableComponents}
                    tableLayout="fixed"
                    dataSource={dataSource}
                    pagination={{
                        pageSize: pageSize,
                        showSizeChanger: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows`,
                        pageSizeOptions: ['50', '100', '200', '500', '1000', '100000'],
                        onShowSizeChange: (current, size) => {
                            setPageSize(size);
                        }
                    }}
                    scroll={{ x: 'max-content', y: 600 }}
                />
            </div>
            <div style={{
                display: "flex",
                gap: "0.75em",
                marginTop: "-3.5em",
                marginBottom: "1em",
                position: "relative",
                width: "0",
                zIndex: 2
            }}>
                <ConditionalTooltip title="Download table data">
                    <button
                        onClick={() => {
                            setPressedDownload(true);
                            setTimeout(() => setPressedDownload(false), 200);
                            const tableData = dataSource;
                            if (tableData && columns) {
                                const header = columns.map((col: any) => escapeCsvValue(col.title)).join(',');
                                const rows = tableData.map((row: any) => columns.map((col: any) => escapeCsvValue(row[col.dataIndex])).join(',')).join('\n');
                                const csvContent = `data:text/csv;charset=utf-8,${encodeURIComponent(`${header}\n${rows}`)}`;
                                const link = document.createElement('a');
                                link.setAttribute('href', csvContent);
                                const filename = parentJobAlias ? `${parentJobAlias}.csv` : 'table_data.csv';
                                link.setAttribute('download', filename);
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                            }
                        }}
                        onMouseEnter={() => setIsDownloadHovered(true)}
                        onMouseLeave={() => setIsDownloadHovered(false)}
                        style={{
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            fontSize: "20px",
                            color: isDownloadHovered
                                ? (darkMode ? "#ffffff" : "#000000")
                                : (darkMode ? "#888888" : "#AFAFAF"),
                            display: "flex",
                            alignItems: "center",
                            padding: "0",
                            transition: "color 0.2s, transform 0.1s",
                            transform: pressedDownload ? "scale(0.85)" : "scale(1)"
                        }}
                    >
                        <DownloadOutlined />
                    </button>
                </ConditionalTooltip>
                <ConditionalTooltip title="Copy table to clipboard">
                    <button
                        onClick={async () => {
                            setPressedCopyTable(true);
                            setTimeout(() => setPressedCopyTable(false), 200);
                            const tableData = dataSource;
                            if (tableData && columns) {
                                const header = columns.map((col: any) => escapeTsvValue(col.title)).join('\t');
                                const rows = tableData.map((row: any) => columns.map((col: any) => escapeTsvValue(row[col.dataIndex])).join('\t')).join('\n');
                                const tsv = `${header}\n${rows}`;
                                try {
                                    await navigator.clipboard.writeText(tsv);
                                    notification.success({
                                        message: 'Copied to clipboard',
                                        description: 'Table has been copied to your clipboard.',
                                        placement: 'bottomLeft',
                                        duration: 2
                                    });
                                } catch (error) {
                                    notification.error({
                                        message: 'Failed to copy',
                                        description: 'Could not copy to clipboard. Please try again.',
                                        placement: 'bottomLeft',
                                        duration: 3
                                    });
                                }
                            }
                        }}
                        onMouseEnter={() => setIsCopyTableHovered(true)}
                        onMouseLeave={() => setIsCopyTableHovered(false)}
                        style={{
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            fontSize: "20px",
                            color: isCopyTableHovered
                                ? (darkMode ? "#ffffff" : "#000000")
                                : (darkMode ? "#888888" : "#AFAFAF"),
                            display: "flex",
                            alignItems: "center",
                            padding: "0",
                            transition: "color 0.2s, transform 0.1s",
                            transform: pressedCopyTable ? "scale(0.85)" : "scale(1)"
                        }}
                    >
                        <CopyOutlined />
                    </button>
                </ConditionalTooltip>
                <ConditionalTooltip title="Add query">
                    <button
                        onClick={() => {
                            setPressedAddQuery(true);
                            setTimeout(() => setPressedAddQuery(false), 200);
                            onAddQuery?.();
                        }}
                        onMouseEnter={() => setIsAddQueryHovered(true)}
                        onMouseLeave={() => setIsAddQueryHovered(false)}
                        style={{
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            fontSize: "20px",
                            color: isAddQueryHovered
                                ? (darkMode ? "#ffffff" : "#000000")
                                : (darkMode ? "#888888" : "#AFAFAF"),
                            display: "flex",
                            alignItems: "center",
                            padding: "0",
                            transition: "color 0.2s, transform 0.1s",
                            transform: pressedAddQuery ? "scale(0.85)" : "scale(1)"
                        }}
                    >
                        <DatabaseOutlined />
                    </button>
                </ConditionalTooltip>
            </div>
        </>
    );
};

